import React, { useState, useEffect, useMemo } from 'react';
import IconSvg from '../../../img/trip_planner/steps/step_7.svg'; // Using step_7 icon temporarily until step_8 is added
import useQuestionTrip from "../../../../../hooks/useQuestionTrip";

interface Step8DestinationProps {
    onStepValidation: (isValid: boolean) => void;
    onUpdateTripProfile: (answerId: number | null) => void;
    initialSelectedId?: number | null;
}

const Step8Destination = ({ onStepValidation, onUpdateTripProfile, initialSelectedId }: Step8DestinationProps) => {
    const [selectedOption, setSelectedOption] = useState<number | null>(initialSelectedId ?? null);
    const { questionsTrip } = useQuestionTrip();
    const [isInitialized, setIsInitialized] = useState(false);

    // Initialize with existing answerId when editing a trip
    useEffect(() => {
        if (!isInitialized && initialSelectedId) {
            setSelectedOption(initialSelectedId);
            setIsInitialized(true);
        }
    }, [initialSelectedId, isInitialized]);

    const options = useMemo(() => {
        const question = questionsTrip?.find(q => q.id === 1111);
        if (!question) return [];

        return question.answers
            .map(answer => ({
                id: answer.id,
                name: answer.name,
                description: answer.description
            }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [questionsTrip]);

    const questionTitle = useMemo(() => {
        const question = questionsTrip?.find(q => q.id === 1111);
        return question?.name || "Select your preference";
    }, [questionsTrip]);

    useEffect(() => {
        onStepValidation(selectedOption !== null);
    }, [selectedOption, onStepValidation]);

    // Only update trip profile after initialization to prevent wiping existing data
    useEffect(() => {
        if (!isInitialized) return;

        const debouncedUpdate = setTimeout(() => {
            onUpdateTripProfile(selectedOption);
        }, 100);

        return () => clearTimeout(debouncedUpdate);
    }, [selectedOption, onUpdateTripProfile, isInitialized]);

    return (
        <div className="space-y-6">
            <div className="text-center mb-8">
                <div className="w-20-h mx-auto mb-4 relative">
                    <img
                        src={IconSvg}
                        alt={questionTitle}
                        className="w-full h-full object-contain"
                    />
                    <div className="absolute inset-0" style={{ backgroundColor: 'var(--primary-color)', mixBlendMode: 'lighten' }} />
                </div>
                <h2 className="text-2xl font-semibold mb-2 syne-fm">{questionTitle}</h2>
            </div>

            <div className="max-w-md mx-auto space-y-3">
                {options.map(option => (
                    <label
                        key={option.id}
                        className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all duration-200 accent-[var(--primary-color)] ${
                            selectedOption === option.id
                                ? 'border-primary-color bg-primary-color-50'
                                : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                    >
                        <input
                            type="radio"
                            name="step8-option"
                            checked={selectedOption === option.id}
                            onChange={() => {
                                if (!isInitialized) setIsInitialized(true);
                                setSelectedOption(option.id);
                            }}
                            className="w-5 h-5 text-primary-color focus:ring-primary-color border-gray-300"
                        />
                        <span className={`text-base ${
                            selectedOption === option.id ? 'text-primary-color font-medium' : 'text-gray-700'
                        }`}>
                            {option.name}
                        </span>
                    </label>
                ))}
            </div>
        </div>
    );
};

export default Step8Destination;